import React, { useState } from "react";
import { CustomTextField } from "../../components/TextFields";
import { Button, InputAdornment } from "@mui/material";
import {
  ModalButtonContainer,
  NegativeModalButton,
  PositiveModalButton,
} from "../../components/Button";
import { useModal } from "../../components/modal";
import objToFromData from "../../utils/objToFromdata";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import ImageUploader from "../../components/uploader/imageUploader";
import { Organ } from "../../types/user";
import * as yup from "yup";
import {
  AddOrganResult,
  useAddOrganMutation,
  useUpdateOrganMutation,
} from "../../app/services/organ.api";
import LoadingButton from "../../components/Button/LoadingButton";
import TextContainer from "../../components/Texts/TextContainer";
import toastError from "../../components/notif";
import { normalizeIrMobile } from "../../utils/phone";

// Shown once after creating an organ: the backend generates a random password
// and returns it only in the create response.
function CreatedOrgan({
  organ,
  onClose,
}: {
  organ: AddOrganResult;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <React.Fragment>
      <TextContainer color="divider" margin="0 0 1rem 0">
        سازمان «{organ.name}» ساخته شد. رمز عبور اولیه را همین حالا یادداشت کنید
        و به سازمان بدهید؛ این رمز دیگر نمایش داده نمی‌شود.
      </TextContainer>
      <CustomTextField
        label="شماره همراه (نام کاربری)"
        variant="outlined"
        value={organ.phone}
        InputProps={{ readOnly: true }}
      />
      <CustomTextField
        label="رمز عبور اولیه"
        variant="outlined"
        value={organ.password}
        InputProps={{
          readOnly: true,
          style: { direction: "ltr", fontFamily: "monospace" },
        }}
      />
      <ModalButtonContainer>
        <Button
          style={NegativeModalButton}
          sx={{
            color: "divider",
            bgcolor: "primary.main",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "divider",
            ":hover": {
              bgcolor: "divider",
              color: "primary.main",
            },
          }}
          onClick={() => {
            navigator.clipboard
              ?.writeText(organ.password)
              .then(() => setCopied(true))
              .catch(() => {});
          }}
        >
          {copied ? "کپی شد" : "کپی رمز"}
        </Button>
        <Button
          style={PositiveModalButton}
          sx={{
            ":hover": {
              bgcolor: "customColors.purple",
              color: "secondary.main",
            },
          }}
          onClick={onClose}
        >
          بستن
        </Button>
      </ModalButtonContainer>
    </React.Fragment>
  );
}

const schema = yup.object().shape({
  name: yup.string().required("نام سازمان الزامی است"),
  phone: yup
    .string()
    .required("شماره تماس الزامی است.")
    .test("ir-mobile", "شماره همراه نادرست است (مثلاً 09123456789).", (v) =>
      !!normalizeIrMobile(v)
    ),
  accountState: yup.string(),
  desc: yup.string(),
});

export function AddModal({
  defaultValue,
}: {
  defaultValue?: Pick<
    Organ,
    "_id" | "name" | "accountState" | "phone" | "desc"
  >;
}) {
  const {
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValue
      ? {
          name: defaultValue?.name,
          accountState: defaultValue?.accountState,
          phone: defaultValue?.phone,
          desc: defaultValue?.desc,
        }
      : {},
  });
  const [profile, setProfile] = useState<File | null>(null);
  const isEdit = defaultValue?._id ? true : false;

  const [addOrgan, addState] = useAddOrganMutation();
  const [updateOrgan, updateState] = useUpdateOrganMutation();
  const [created, setCreated] = useState<AddOrganResult | null>(null);

  const { setShow } = useModal();
  if (created)
    return <CreatedOrgan organ={created} onClose={() => setShow(false)} />;
  return (
    <React.Fragment>
      <CustomTextField
        required
        label="نام سازمان"
        placeholder="نام سازمان را وارد کنید."
        variant="outlined"
        defaultValue={getValues("name")}
        onChange={(e) => setValue("name", e.target.value)}
        error={!!errors.name}
        helperText={errors.name?.message}
      />
      <ImageUploader
        title="تصویر (اختیاری)"
        onChange={(file) => setProfile(file)}
      />

      <CustomTextField
        label="شماره همراه"
        defaultValue={getValues("phone")}
        onChange={(e) => setValue("phone", e.target.value)}
        error={!!errors.phone}
        helperText={errors.phone?.message}
        required
        variant="outlined"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {/* <PersonIcon sx={{ color: "divider", padding: "5px" }} /> */}
            </InputAdornment>
          ),
        }}
      />
      <CustomTextField
        label="توضیحات"
        placeholder="توضیحات را وارد کنید."
        variant="outlined"
        defaultValue={getValues("desc")}
        multiline
        minRows={2}
        maxRows={4}
        onChange={(e) => setValue("desc", e.target.value)}
        error={!!errors.desc}
        helperText={errors.desc?.message}
      />
      <ModalButtonContainer>
        <Button
          style={NegativeModalButton}
          sx={{
            color: "divider",
            bgcolor: "primary.main",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: "divider",
            ":hover": {
              bgcolor: "divider",
              color: "primary.main",
            },
          }}
          onClick={() => setShow(false)}
        >
          انصراف
        </Button>
        <LoadingButton
          style={PositiveModalButton}
          sx={{
            ":hover": {
              bgcolor: "customColors.purple",
              color: "secondary.main",
            },
          }}
          isLoading={addState.isLoading || updateState.isLoading}
          onClick={handleSubmit(
            (data) => {
            // backend validates IR numbers; send the normalised 09xxxxxxxxx form
            const formData = objToFromData({
              ...data,
              phone: normalizeIrMobile(data.phone) ?? data.phone,
            });
            if (profile) formData.append("profile", profile);
            if (isEdit && defaultValue) {
              return updateOrgan({
                organId: defaultValue._id,
                data: formData,
              })
                .unwrap()
                .then(() => setShow(false))
                .catch(() => {});
            }

            addOrgan(formData)
              .unwrap()
              .then((res) => {
                if (res?.password) setCreated(res);
                else setShow(false);
              })
              .catch(() => {}); // organ.api shows the server message as a toast
            },
            (errs) =>
              toastError(
                "فرم کامل نیست: " +
                  Object.values(errs)
                    .map((e: any) => e?.message)
                    .filter(Boolean)
                    .join("، ")
              )
          )}
        >
          تایید
        </LoadingButton>
      </ModalButtonContainer>
    </React.Fragment>
  );
}
